import type { Entry, PaymentMethod, VendorPaymentHistoryRow } from "@/api/types";

export interface DerivedPaymentAnomaly {
  attempted: PaymentMethod;
  previouslyUsed: PaymentMethod[];
}

/**
 * Client-side echo of the server's payment-anomaly check
 * (`DemoRepository.CheckPaymentMethodAsync`), for the laptop dashboard row.
 *
 * WHY THIS EXISTS: `POST /api/demo/entries` returns the anomaly directly in
 * its response (see api/types.ts `CreateEntryResponse.anomaly`), which is
 * what the phone's own success screen renders — that copy is authoritative,
 * straight off the wire. But `GET /api/demo/entries` (what the dashboard
 * polls) does NOT carry it; `EntryResponse` has no anomaly field, and this
 * task is scoped to `web/` only, so the response shape cannot change here.
 * The dashboard needs the same signal on a device that never saw the
 * original POST response, so this recomputes it from data already on the
 * wire (the entries feed + the payment-methods list) rather than duplicating
 * or guessing at server state — a derived value, not a second source of truth.
 *
 * Mirrors the server's rule exactly: for a given entry, look at every OTHER
 * entry for the same vendor created strictly before it; if that vendor has
 * prior payment-method history and this entry's instrument isn't in it,
 * it's an anomaly. A vendor with no prior history (or no history that
 * differs) is never flagged — "first time" is not "different than usual".
 *
 * Known limitations, both acceptable for a booth-table dashboard badge:
 *   - Only sees whatever `GET /api/demo/entries` returned (default limit
 *     25) — a vendor's history older than that window won't be found.
 *   - Only sees payment methods (`GET /api/demo/payment-methods`); if that
 *     route is unavailable (404, see hooks/useReferenceData.ts), this
 *     always returns null rather than guessing from ids alone.
 */
export function derivePaymentAnomaly(
  entry: Entry,
  allEntries: readonly Entry[],
  paymentMethodsById: ReadonlyMap<string, PaymentMethod>,
  /**
   * The vendor's payment history from the LEDGER, which is the whole tenant rather than the
   * page of it `allEntries` happens to hold.
   *
   * Optional and additive: with it absent this behaves exactly as it always did, which is
   * what keeps every existing caller and test honest. MEASURED 2026-09-08 — a vendor's two
   * prior Visa rows scrolled out of the twenty-five-row feed and beat 3 stopped firing on
   * the wall while the create endpoint was still returning the anomaly correctly. The rule
   * below was never wrong; it was starved.
   *
   * BOTH sources are used, not one instead of the other: the ledger snapshot is fetched once
   * per session and cannot know about an arrival from thirty seconds ago, while `allEntries`
   * always can.
   */
  ledgerHistory: readonly VendorPaymentHistoryRow[] = [],
): DerivedPaymentAnomaly | null {
  if (!entry.vendorId || !entry.paymentMethodId) return null;

  const attempted = paymentMethodsById.get(entry.paymentMethodId);
  if (!attempted) return null;

  const entryTime = new Date(entry.createdAt).getTime();
  const seen = new Map<string, PaymentMethod>();

  for (const other of allEntries) {
    if (other.id === entry.id) continue;
    if (other.vendorId !== entry.vendorId) continue;
    if (!other.paymentMethodId) continue;
    if (new Date(other.createdAt).getTime() >= entryTime) continue; // only prior history

    const method = paymentMethodsById.get(other.paymentMethodId);
    if (method) seen.set(method.id, method);
  }

  for (const row of ledgerHistory) {
    if (row.vendorId !== entry.vendorId) continue;
    // "Used BEFORE this entry" — a card that first appeared later is not what the vendor
    // usually pays with, and treating it as history would flag the wrong row.
    if (new Date(row.firstUsedAt).getTime() >= entryTime) continue;
    const method = paymentMethodsById.get(row.paymentMethodId);
    if (method) seen.set(method.id, method);
  }

  if (seen.size === 0) return null; // no history to be inconsistent with
  if (seen.has(attempted.id)) return null; // consistent with something seen before

  return {
    attempted,
    previouslyUsed: [...seen.values()].sort((a, b) => a.label.localeCompare(b.label)),
  };
}
